import type {
  ConfirmMeetingFoodCategoryModel,
  MeetingCancelModel,
  MeetingListModel,
  MeetingModel,
  MeetingRequestModel,
  MeetingRescheduleModel,
  MeetingUpdateModel,
  MeetingWithFoodCategoryModel,
  UpdateMeetingAttendeeStatusModel,
} from '../app/types/meeting.type.ts';
import type { StaffMemberListModel } from '../app/types/staff-member.type.ts';
import baseApi, { CacheTag } from './meeting-md-api-slice.ts';

const meetingsApiSlice = baseApi.injectEndpoints({
  endpoints: builder => ({
    fetchRequestedOrUpcomingApprovedMeetings: builder.query<
      MeetingListModel[],
      { accountKey: string | undefined; upcomingApprovedDaysOut?: number }
    >({
      query: ({ accountKey, upcomingApprovedDaysOut }) => ({
        url: '/Meeting/RequestedOrUpcomingApproved',
        params: {
          accountKey,
          upcomingApprovedDaysOut,
        },
      }),
      providesTags: [CacheTag.Events],
    }),
    fetchAccountDietaryInformation: builder.query<StaffMemberListModel[], string>({
      query: key => `/Meeting/${key}/AccountDietaryInformation`,
    }),
    fetchAccountFoodCategorySchedule: builder.query<
      MeetingWithFoodCategoryModel[],
      { accountKey: string; date: string }
    >({
      query: ({ accountKey, date }) => ({
        url: '/Meeting/AccountFoodCategorySchedule',
        params: { accountKey, date },
      }),
    }),
    fetchMeeting: builder.query<MeetingModel, string>({
      query: key => ({ url: `/meeting/${key}` }),
      providesTags: [CacheTag.Events],
    }),
    fetchMeetingByAvailability: builder.query<MeetingModel, string>({
      query: key => ({ url: `/meeting/availability/${key}` }),
      providesTags: [CacheTag.Events],
    }),
    postRequestMeeting: builder.mutation<MeetingModel, MeetingRequestModel>({
      query: model => ({
        url: '/Meeting/Request',
        method: 'POST',
        body: model,
      }),
      invalidatesTags: [CacheTag.Events],
    }),
    putUpdateMeeting: builder.mutation<MeetingModel, { key: string; model: MeetingUpdateModel }>({
      query: ({ key, model }) => ({
        url: `/Meeting/${key}/Update`,
        method: 'PUT',
        body: model,
      }),
      invalidatesTags: [CacheTag.Events],
    }),
    putConfirmMeetingFoodCategory: builder.mutation<
      MeetingModel,
      { key: string; model: ConfirmMeetingFoodCategoryModel }
    >({
      query: ({ key, model }) => ({
        url: `/Meeting/${key}/ConfirmFoodCategory`,
        method: 'PUT',
        body: model,
      }),
      invalidatesTags: [CacheTag.Events],
    }),
    putUpdateMeetingAttendeeStatus: builder.mutation<
      MeetingModel,
      { key: string; model: UpdateMeetingAttendeeStatusModel }
    >({
      query: ({ key, model }) => ({
        url: `/Meeting/${key}/UpdateMeetingAttendeeStatus`,
        method: 'PUT',
        body: model,
      }),
      invalidatesTags: [CacheTag.Events],
    }),
    putApproveMeeting: builder.mutation<MeetingModel, { key: string }>({
      query: ({ key }) => ({
        url: `/Meeting/${key}/Approve`,
        method: 'PUT',
      }),
      invalidatesTags: [CacheTag.Events],
    }),
    putCancelMeeting: builder.mutation<MeetingModel, { key: string; model: MeetingCancelModel }>({
      query: ({ key, model }) => ({
        url: `/Meeting/${key}/Cancel`,
        method: 'PUT',
        body: model,
      }),
      invalidatesTags: [CacheTag.Events],
    }),
    putRescheduleMeeting: builder.mutation<
      MeetingModel,
      { key: string; model: MeetingRescheduleModel }
    >({
      query: ({ key, model }) => ({
        url: `/Meeting/${key}/Reschedule`,
        method: 'PUT',
        body: model,
      }),
      invalidatesTags: [CacheTag.Events],
    }),
  }),
});

export const {
  useFetchRequestedOrUpcomingApprovedMeetingsQuery,
  useFetchAccountDietaryInformationQuery,
  useFetchAccountFoodCategoryScheduleQuery,
  useFetchMeetingQuery,
  usePostRequestMeetingMutation,
  useFetchMeetingByAvailabilityQuery,
  usePutUpdateMeetingMutation,
  usePutConfirmMeetingFoodCategoryMutation,
  usePutUpdateMeetingAttendeeStatusMutation,
  usePutApproveMeetingMutation,
  usePutCancelMeetingMutation,
  usePutRescheduleMeetingMutation,
} = meetingsApiSlice;
